import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ReviewStatusBadge from "@/components/ReviewStatusBadge";
import VerificationDocList, { ProgressBar } from "@/components/VerificationDocList";

const SUBMITTABLE = ["draft", "pending_documents", "additional_info_required"];

export function DevelopmentDocumentsPanel({ devId, onSubmitted }) {
  const [st, setSt] = useState(null);
  const [company, setCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    const [d, c] = await Promise.all([api.get(`/developments/${devId}/documents`), api.get("/verification/company")]);
    setSt(d.data); setCompany(c.data);
  }
  useEffect(() => { load(); }, [devId]);

  async function submit() {
    setSubmitting(true);
    try {
      await api.post(`/developments/${devId}/submit-for-review`);
      toast.success("Submitted for review");
      await load();
      onSubmitted && onSubmitted();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  }

  if (!st) return <p className="text-sm text-neutral-500 py-8 text-center">Loading documents…</p>;
  const canSubmit = SUBMITTABLE.includes(st.review_status);
  return (
    <div className="space-y-4" data-testid="development-documents-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <ReviewStatusBadge status={st.review_status} />
        <p className="text-xs text-neutral-500">Requirements for {st.country || "your country"}</p>
      </div>
      {company && !company.verified && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 flex gap-3" data-testid="company-not-verified-warning">
          <ShieldAlert size={18} className="flex-shrink-0 mt-0.5" />
          <p>Your company isn't verified yet. This development can't go live until you finish <Link to="/verification" className="font-semibold underline">company verification</Link>.</p>
        </div>
      )}
      <div className="soft-card p-5"><ProgressBar done={st.required_uploaded} total={st.required_total} testId="dev-docs-progress" /></div>
      <VerificationDocList items={st.items} uploadUrl={`/developments/${devId}/documents`} onChanged={load} />
      {canSubmit && (
        <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
          <p className="text-xs text-neutral-500">{st.all_uploaded ? "All mandatory documents uploaded." : `Missing: ${st.missing.join(", ")}`}</p>
          <button data-testid="dev-docs-submit" onClick={submit} disabled={!st.all_uploaded || submitting} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-propera hover:bg-propera-hover text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={14} /> {submitting ? "Submitting…" : "Submit for Review"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function DevelopmentDocumentsModal({ dev, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm animate-fade-in" data-testid="development-documents-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-fade-up">
        <div className="px-5 sm:px-8 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">Development documents</h2>
            <p className="text-xs text-neutral-500">{dev.name}</p>
          </div>
          <button data-testid="dev-docs-close" onClick={onClose} className="p-2 rounded-full hover:bg-neutral-100"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5">
          <DevelopmentDocumentsPanel devId={dev.id} />
        </div>
      </div>
    </div>
  );
}
